import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { StoreSalesPerformanceResponse, useElementSize } from '../../../../../hooks';
import { ColumnFlexContainer } from '../../../../../stories/atoms/containerV2/ContainerV2';
import { storeOverviewTranslations } from '../../../../../translations';
import { StoreWidgetContent } from '../../../../storeOverviewPage/storeWidgetContent/StoreWidgetContent';
import { ToggleChipGroup } from '../../../common/toggleChipGroup/ToggleChipGroup';
import { NoDataForPeriodView } from '../noDataForPeriodView/NoDataForPeriodView';
import { chipDataMapping, ChipKey, ChipOption, useChipOptionsMap } from '../sales.util';
import { PotentialSection } from './potentialSection/PotentialSection';
import { getTimePeriodToPotentialDataMap, POTENTIAL_SECTION_MIN_WIDTH, POTENTIAL_CONTAINER_GAP } from './util';

const PotentialContainer = styled.div<{ gridWidth: string }>`
  max-height: ${({ theme }) => theme.spacing(66)};
  display: grid;
  gap: ${({ theme }) => theme.spacing(2)};
  box-sizing: border-box;
  grid-template-columns: ${({ gridWidth }) => gridWidth};
  overflow: auto;
`;

type TopPotentialWidgetProps = {
  salesPerformanceData: StoreSalesPerformanceResponse | null;
  isLoading: boolean;
  isError: boolean;
  selectedFilter: string | undefined;
  retry: () => void;
};

export const TopPotentialWidget: FC<TopPotentialWidgetProps> = ({
  salesPerformanceData,
  isLoading,
  isError,
  selectedFilter,
  retry,
}) => {
  const { t } = useTranslation();
  const { topPotential: topPotentialTranslations } = storeOverviewTranslations;
  const { chipOptionsMap } = useChipOptionsMap();
  const [selectedChip, setSelectedChip] = useState<ChipKey>('LCD');
  const { ref: containerRef, width } = useElementSize<HTMLDivElement>();

  const timePeriodToPotentialDataMap = useMemo(
    () => (salesPerformanceData ? getTimePeriodToPotentialDataMap(salesPerformanceData, selectedFilter) : null),
    [salesPerformanceData, selectedFilter],
  );

  const chipOptions: ChipOption[] = [
    chipOptionsMap.LCD,
    chipOptionsMap.CW,
    chipOptionsMap.LCW,
    chipOptionsMap.L4Wks,
    chipOptionsMap.AccTY,
  ];

  const defaultPeriodData = {
    index: [],
    subIndex: [],
    garmentGroup: [],
    department: [],
  };

  const potentialData = useMemo(() => {
    if (!timePeriodToPotentialDataMap) return defaultPeriodData;

    const selectedPotentialData = timePeriodToPotentialDataMap[chipDataMapping[selectedChip]];

    return selectedPotentialData;
  }, [timePeriodToPotentialDataMap, selectedChip]);

  const gridTemplateColumns = useMemo(() => {
    if (width > POTENTIAL_SECTION_MIN_WIDTH * 4 * 3 + POTENTIAL_CONTAINER_GAP * 2) {
      return 'repeat(3, minmax(240px, 1fr))';
    }
    if (width >= POTENTIAL_SECTION_MIN_WIDTH * 4 * 2 + POTENTIAL_CONTAINER_GAP) {
      return 'repeat(2, 1fr)';
    }
    return '1fr';
  }, [width]);

  const noDataViewForTimePeriod = useMemo(() => {
    const { index, subIndex, garmentGroup, department } = potentialData;
    return index.length === 0 && subIndex.length === 0 && garmentGroup.length === 0 && department.length === 0;
  }, [potentialData]);

  return (
    <StoreWidgetContent
      widgetVariant="default"
      title={t(topPotentialTranslations.title.key, topPotentialTranslations.title.defaultValue)}
      tooltipText={t(topPotentialTranslations.tooltip.key, topPotentialTranslations.tooltip.defaultValue)}
      errorRetryHandler={retry}
      isLoading={isLoading}
      isError={isError}
      isNoData={!timePeriodToPotentialDataMap}
      errorText={t(topPotentialTranslations.errorText.key, topPotentialTranslations.errorText.defaultValue)}
      widgetContent={
        <ColumnFlexContainer height="100%">
          <ToggleChipGroup
            chipOptions={chipOptions}
            selectedChip={selectedChip}
            setSelectedChip={(chip: ChipKey) => {
              setSelectedChip(chip);
            }}
          />

          {noDataViewForTimePeriod && <NoDataForPeriodView />}

          {!noDataViewForTimePeriod && (
            <PotentialContainer ref={containerRef} gridWidth={gridTemplateColumns}>
              {!selectedFilter && (
                <PotentialSection
                  title={t(
                    topPotentialTranslations.indexesText.key,
                    topPotentialTranslations.indexesText.defaultValue,
                  )}
                  subTitle={t(
                    topPotentialTranslations.topPontialIndexesText.key,
                    topPotentialTranslations.topPontialIndexesText.defaultValue,
                  )}
                  data={potentialData.index}
                  showDescription
                  fullWidth
                />
              )}
              {selectedFilter && (
                <PotentialSection
                  title={t(
                    topPotentialTranslations.subIndexesText.key,
                    topPotentialTranslations.subIndexesText.defaultValue,
                  )}
                  subTitle={t(
                    topPotentialTranslations.topPontialSubIndexesText.key,
                    topPotentialTranslations.topPontialSubIndexesText.defaultValue,
                  )}
                  data={potentialData.subIndex}
                  showDescription
                  fullWidth
                />
              )}
              <PotentialSection
                title={t(
                  topPotentialTranslations.garmentGroupsText.key,
                  topPotentialTranslations.garmentGroupsText.defaultValue,
                )}
                subTitle={t(
                  topPotentialTranslations.topPontialGarmentGroupsText.key,
                  topPotentialTranslations.topPontialGarmentGroupsText.defaultValue,
                )}
                data={potentialData.garmentGroup}
                showDescription={false}
                fullWidth
              />
              <PotentialSection
                title={t(
                  topPotentialTranslations.departmentsText.key,
                  topPotentialTranslations.departmentsText.defaultValue,
                )}
                subTitle={t(
                  topPotentialTranslations.topPontialDepartmentsText.key,
                  topPotentialTranslations.topPontialDepartmentsText.defaultValue,
                )}
                data={potentialData.department}
                showDescription
                fullWidth={false}
              />
            </PotentialContainer>
          )}
        </ColumnFlexContainer>
      }
    />
  );
};
